import { useId, useState } from "react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { pickLocalized } from "@/lib/i18n/localized";
import { keys } from "@/lib/i18n/portal";
import type { SubscriptionPlanType, SubscriptionPlanView } from "@/lib/api/types";
import { SUBSCRIPTION_PLAN_TYPE } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { Banner, Button, Drawer, Field, Input, Select, Toggle, useToast } from "@/shared/rd";
import { useCreateSubscriptionPlanMutation } from "../services/useCreateSubscriptionPlanMutation";
import { useUpdateSubscriptionPlanMutation } from "../services/useUpdateSubscriptionPlanMutation";
import { PER_ORDER_PLAN_AMOUNT, PER_ORDER_PLAN_PERIOD_DAYS } from "../lib/per-order-plan-defaults";
import { PLAN_TYPE_ORDER, isFreePlanType, planTypeLabelKey } from "../lib/plan-display";

type Props = {
  open: boolean;
  /** null creates a new plan. */
  plan: SubscriptionPlanView | null;
  onClose: () => void;
};

/**
 * Create and edit a subscription plan in one drawer over the catalogue.
 * Mount it with a fresh `key` per opening so its state starts from `plan`.
 * Saved → a shell toast naming the plan; failed → a Banner at the top of the
 * form with Try again, the fields as they were.
 */
export function SubscriptionPlanEditor({ open, plan, onClose }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const locale = useLocale();
  const formId = useId();
  const isEdit = plan !== null;
  const [nameAr, setNameAr] = useState(plan?.nameAr ?? "");
  const [nameEn, setNameEn] = useState(plan?.nameEn ?? "");
  const [planType, setPlanType] = useState<SubscriptionPlanType>(
    plan?.type ?? SUBSCRIPTION_PLAN_TYPE.MONTHLY_SUBSCRIPTION,
  );
  const [amountValue, setAmountValue] = useState(plan ? String(plan.amount) : "0");
  // What the amount goes back to when the type moves off a free type.
  const [lastPaidAmount, setLastPaidAmount] = useState(plan ? String(plan.amount) : "0");
  const [periodValue, setPeriodValue] = useState(plan ? String(plan.periodDays) : "");
  const [perOrderValue, setPerOrderValue] = useState(
    plan?.perOrderAmount === null || plan?.perOrderAmount === undefined ? "" : String(plan.perOrderAmount),
  );
  const [isActive, setIsActive] = useState(plan?.isActive ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const create = useCreateSubscriptionPlanMutation();
  const update = useUpdateSubscriptionPlanMutation();
  const isSubmitting = create.isPending || update.isPending;

  const isFree = isFreePlanType(planType);
  // The per-order price only exists on this plan type; the backend rejects it
  // anywhere else, so the field is mounted only here and sent as null otherwise.
  const isPerOrderCredit = planType === SUBSCRIPTION_PLAN_TYPE.PER_ORDER_CREDIT;

  const clearError = (field: string) =>
    setErrors((current) => {
      if (!(field in current)) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });

  const handleTypeChange = (nextType: SubscriptionPlanType) => {
    setPlanType(nextType);
    clearError("perOrderAmount");
    clearError("amount");
    if (isFreePlanType(nextType)) {
      if (amountValue.trim() && amountValue !== "0") {
        setLastPaidAmount(amountValue);
      }
      setAmountValue("0");
      return;
    }
    setAmountValue(lastPaidAmount);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    const nextErrors: Record<string, string> = {};
    if (!nameAr.trim()) nextErrors.nameAr = t(keys.errors.required);

    // Mirrors the backend rule so the admin sees the problem before the 400.
    let perOrderAmount: number | null = null;
    if (isPerOrderCredit) {
      const raw = perOrderValue.trim();
      const parsed = Number(raw);
      if (!raw) nextErrors.perOrderAmount = t(keys.admin.subscriptionPlanPerOrderAmountRequired);
      else if (!Number.isFinite(parsed) || parsed <= 0)
        nextErrors.perOrderAmount = t(keys.admin.subscriptionPlanPerOrderAmountPositive);
      else perOrderAmount = parsed;
    } else {
      if (!isFree && !amountValue.trim()) nextErrors.amount = t(keys.errors.required);
      if (!periodValue.trim()) nextErrors.periodDays = t(keys.errors.required);
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const body = {
      nameAr,
      nameEn,
      type: planType,
      // A per-order plan has no period price and no period — neither field is
      // mounted, and safe defaults go over the wire for the NOT NULL columns.
      amount: isPerOrderCredit ? PER_ORDER_PLAN_AMOUNT : isFree ? 0 : Number(amountValue),
      periodDays: isPerOrderCredit ? PER_ORDER_PLAN_PERIOD_DAYS : Number(periodValue),
      perOrderAmount,
      isActive,
    };

    try {
      if (plan) {
        await update.mutateAsync({ subscriptionId: plan.id, ...body });
      } else {
        await create.mutateAsync(body);
      }
    } catch (caught) {
      setFormError(caught instanceof ApiJsonClientError ? caught.message || "" : "");
      return;
    }
    const name = pickLocalized(locale, nameAr.trim(), nameEn.trim());
    toast.show({
      message: t(isEdit ? "admin.subscriptionPlanSavedNamedToast" : "admin.subscriptionPlanCreatedNamedToast", { name }),
    });
    onClose();
  };

  const crumbName = plan ? pickLocalized(locale, plan.nameAr, plan.nameEn) : t("admin.subscriptionPlanNewCrumb");

  return (
    <Drawer
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      closeLabel={t(keys.common.close)}
      width={520}
      crumb={`${t(keys.admin.subscriptionsTitle)} / ${crumbName}`}
      badge="bill"
      title={t(isEdit ? keys.admin.subscriptionEditTitle : keys.admin.subscriptionCreateTitle)}
      sub={t(isEdit ? keys.admin.subscriptionEditHint : keys.admin.subscriptionCreateHint)}
      actions={
        <Button
          type="submit"
          form={formId}
          variant="primary"
          size="sm"
          loading={isSubmitting}
          loadingLabel={t(isEdit ? "admin.subscriptionPlanSaving" : "admin.subscriptionPlanCreating")}
        >
          {t(isEdit ? keys.common.save : keys.common.create)}
        </Button>
      }
    >
      <form id={formId} noValidate onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {formError !== null ? (
          <Banner
            tone="error"
            title={t(isEdit ? "admin.subscriptionPlanSaveFailedTitle" : "admin.subscriptionPlanCreateFailedTitle")}
            actions={
              <Button
                type="submit"
                size="sm"
                loading={isSubmitting}
                loadingLabel={t(keys.shell.retrying)}
              >
                {t(keys.shell.retry)}
              </Button>
            }
          >
            {[formError, t("admin.subscriptionPlanNothingChanged")].filter(Boolean).join(" ")}
          </Banner>
        ) : null}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 12 }}>
          <Field label={t(keys.admin.subscriptionPlanNameAr)} htmlFor={`${formId}-nameAr`} error={errors.nameAr}>
            <Input
              id={`${formId}-nameAr`}
              name="nameAr"
              dir="rtl"
              required
              value={nameAr}
              invalid={Boolean(errors.nameAr)}
              placeholder={t(keys.placeholders.name)}
              onChange={(event) => {
                setNameAr(event.target.value);
                clearError("nameAr");
              }}
            />
          </Field>
          <Field label={t(keys.admin.subscriptionPlanNameEn)} htmlFor={`${formId}-nameEn`}>
            <Input
              id={`${formId}-nameEn`}
              name="nameEn"
              value={nameEn}
              placeholder={t(keys.placeholders.name)}
              onChange={(event) => setNameEn(event.target.value)}
            />
          </Field>
        </div>

        <Field label={t(keys.admin.subscriptionPlanType)} htmlFor={`${formId}-type`}>
          <Select
            id={`${formId}-type`}
            name="type"
            value={planType}
            style={{ maxWidth: 240 }}
            onChange={(event) => handleTypeChange(event.target.value as SubscriptionPlanType)}
            options={PLAN_TYPE_ORDER.map((type) => ({ value: type, label: t(planTypeLabelKey(type)) }))}
          />
        </Field>

        {isPerOrderCredit ? (
          <Field
            label={t(keys.admin.subscriptionPlanPerOrderAmount)}
            htmlFor={`${formId}-perOrder`}
            help={t(keys.admin.subscriptionPlanPerOrderAmountHelper)}
            error={errors.perOrderAmount}
          >
            <Input
              id={`${formId}-perOrder`}
              name="perOrderAmount"
              type="number"
              inputMode="decimal"
              min={0}
              step={0.01}
              placeholder="0"
              value={perOrderValue}
              invalid={Boolean(errors.perOrderAmount)}
              style={{ width: 180 }}
              onChange={(event) => {
                setPerOrderValue(event.target.value);
                clearError("perOrderAmount");
              }}
            />
          </Field>
        ) : (
          <div className="rd-fields-inline">
            <Field
              label={t(keys.admin.subscriptionPlanAmount)}
              htmlFor={`${formId}-amount`}
              help={isFree ? t(keys.admin.subscriptionPlanAmountFreeTrialHelper) : undefined}
              error={errors.amount}
            >
              <Input
                id={`${formId}-amount`}
                name="amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                placeholder="0"
                value={amountValue}
                disabled={isFree}
                invalid={Boolean(errors.amount)}
                style={{ width: 160 }}
                onChange={(event) => {
                  setAmountValue(event.target.value);
                  if (!isFree) setLastPaidAmount(event.target.value);
                  clearError("amount");
                }}
              />
            </Field>
            <Field label={t(keys.admin.subscriptionPlanPeriodDays)} htmlFor={`${formId}-period`} error={errors.periodDays}>
              <Input
                id={`${formId}-period`}
                name="periodDays"
                type="number"
                inputMode="numeric"
                placeholder="30"
                value={periodValue}
                invalid={Boolean(errors.periodDays)}
                style={{ width: 120 }}
                onChange={(event) => {
                  setPeriodValue(event.target.value);
                  clearError("periodDays");
                }}
              />
            </Field>
          </div>
        )}

        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
          <Toggle checked={isActive} onChange={setIsActive} label={t(keys.admin.subscriptionPlanIsActive)} />
          {t(keys.admin.subscriptionPlanIsActive)}
        </label>
      </form>
    </Drawer>
  );
}
